import {truncateAt} from "@cm/utils"
import type {BaseGraphNodeDefinition, NodeParameters} from "#graph/nodes/base/types"
import type {VersionChain} from "#graph/versioning/types"
import {v4 as uuid4} from "uuid"
import {z} from "zod/v4-mini"
import {traverseGraphParameters} from "#graph/helpers/parameters"

export enum TraversalAction {
    Continue,
    StopDescend,
    StopCompletely,
}

export abstract class BaseGraphNode<out Parameters extends NodeParameters = NodeParameters, out ConstructorParameters extends NodeParameters = Parameters> {
    public abstract get definition(): BaseGraphNodeDefinition<Parameters, ConstructorParameters>

    instanceId = uuid4()
    public parameters: Readonly<Parameters>

    public constructor(options: {parameters: ConstructorParameters; disableValidation?: false} | {parameters: Parameters; disableValidation: true}) {
        this.parameters = options.disableValidation ? options.parameters : this.ensureValidParameters(options.parameters)
    }

    public toJSON(): object {
        return {
            [this.nodeClass]: {
                id: this.id,
                ...this.parameters,
            },
        }
    }

    public toString() {
        return `${this.nodeClass}<${this.instanceId}>: ${truncateAt(JSON.stringify(this.parameters))}`
    }

    get id() {
        return this.instanceId
    }

    public get paramsSchema() {
        return this.definition.validation.parameters
    }

    public get label() {
        return this.definition.label
    }

    public get nodeClass() {
        return this.definition.nodeClass
    }

    // Class-level accessor — returns the concrete subclass's `nodeClass` without instantiation.
    // Works because subclasses implement `get definition()` as a no-op `return constantDefinition`,
    // which is safely callable on the prototype itself.
    public static getNodeClass(this: {prototype: {definition: {nodeClass: string}}}): string {
        return this.prototype.definition.nodeClass
    }

    public get nodeVersion() {
        const versionChain: VersionChain = this.definition.versionChain ?? []
        if (!Array.isArray(versionChain)) throw new Error(`Version chain should be an array, got ${versionChain}`)
        return versionChain.length
    }

    replaceParameters(newParameters: Parameters, disableValidation: true): void
    replaceParameters(newParameters: ConstructorParameters, disableValidation?: false): void
    replaceParameters(newParameters: ConstructorParameters | Parameters, disableValidation = false): void {
        this.parameters = (disableValidation ? newParameters : this.ensureValidParameters(newParameters as ConstructorParameters)) as Parameters
    }

    updateParameters(newParameters: Partial<Parameters>, disableValidation: true): void
    updateParameters(newParameters: Partial<ConstructorParameters>, disableValidation?: false): void
    updateParameters(newParameters: Partial<Parameters> | Partial<ConstructorParameters>, disableValidation = false): void {
        this.replaceParameters({...this.parameters, ...newParameters} as Parameters, disableValidation as true)
    }

    ensureValidParameters(parameters?: ConstructorParameters): Parameters {
        const result = this.paramsSchema.safeParse(parameters ?? this.parameters)
        if (result.success) {
            return result.data
        } else {
            throw new Error(`Invalid parameters for node ${this.nodeClass}: ${z.prettifyError(result.error)}`, {
                cause: {parameters, issues: result.error.issues, schema: this.paramsSchema},
            })
        }
    }

    //For illustration of orders, see: https://en.wikipedia.org/wiki/Tree_traversal
    depthFirstTraversalPreorder(callback: (node: BaseGraphNode) => TraversalAction, visited: Set<BaseGraphNode> = new Set()): boolean {
        if (visited.has(this)) return true
        visited.add(this)

        const action = callback(this)
        if (action === TraversalAction.StopCompletely) return false
        if (action !== TraversalAction.StopDescend) {
            let stop = false
            traverseGraphParameters(
                this.parameters,
                (value) => value instanceof BaseGraphNode,
                (child: BaseGraphNode) => {
                    if (!child.depthFirstTraversalPreorder(callback, visited)) stop = true
                },
            )

            if (stop) return false
        }

        return true
    }
}
